/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type {
  InitialData as DispatchDriverSearchFormInitialData,
  ValidData as DispatchDriverSearchFormValidData,
} from '@/components/searchForms/DispatchDriverSearchForm.vue';
import type {
  InitialData as DispatchJobSearchFormInitialData,
  ValidData as DispatchJobSearchFormValidData,
} from '@/components/searchForms/DispatchJobSearchForm.vue';
import type { Model as DispatchJobsTableModel } from '@/components/tables/DispatchJobsTable.vue';
import type { DispatchDashboardViewDataComposable } from '@/composables/data/useDispatchDashboardViewData';
import type {
  DefRouteQuery as DispatchDefRouteQuery,
  RouteQueryComposable as DispatchRouteQueryComposable,
} from '@/composables/routeQueries/useDispatchRouteQuery';
// import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';
import { watchDebounced } from '@vueuse/core';
import { format } from 'date-fns';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import { useJobEditDialog } from '@/composables/useJobEditDialog';
import { useStaticStore } from '@/stores/useStaticStore';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as dispatchDriversTableHelpers from '@/helpers/tables/dispatchDrivers';
import * as dispatchJobsTableHelpers from '@/helpers/tables/dispatchJobs';
import * as commonConstants from '@/constants/common';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateDispatchDashboardViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<DispatchDashboardViewDataComposable>>>
  >;
  query: RequiredKeys<DispatchDefRouteQuery>;
}

interface DefDispatchDashboardViewState {
  jobsTableModel: DispatchJobsTableModel;
  stopDetailsActivatedTabNum: number;
  bulkSelectedJobIds: number[];
  selectedJobId: number;
}

const defPrivateDispatchDashboardViewState: DefPrivateDispatchDashboardViewState =
  {
    data: {
      isFetchingDispatchJobs: undefined,
      isFetchingStopLocations: undefined,
      isAwaitingStopLocations: undefined,
      dispatchJobs: undefined,
      stopLocations: undefined,
      dispatchJobsLocks: undefined,
      dispatchDriversMarks: undefined,
      dispatchJobsError: undefined,
      stopLocationsError: undefined,
      refreshDispatchJobs: undefined,
      refreshStopLocations: undefined,
      reload: undefined,
    },
    query: {
      tab: 'unallocated', // TODO undefined?
      date: undefined,
      jobSearch: undefined,
      jobServiceAreas: undefined,
      jobServiceGroups: undefined,
      jobSites: undefined,
      jobGoods: undefined,
      isJobCanSchedule: undefined,
      isJobPayOnHold: undefined,
      isJobInvoice: undefined,
      driverSearch: undefined,
      driverFleets: undefined,
      driverType: undefined,
      driverZone: undefined,
    },
  };

const defDispatchDashboardViewState: DefDispatchDashboardViewState = {
  jobsTableModel: {
    active: NaN,
    select: {},
  },
  stopDetailsActivatedTabNum: 1,
  bulkSelectedJobIds: [],
  selectedJobId: NaN,
};

function privateDispatchDashboardViewStoreSetup(
  helpers: PiniaSetupStoreHelpers,
) {
  const state = reactive(_cloneDeep(defPrivateDispatchDashboardViewState));
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const route = useRoute() || {}; // TODO mock useRoute

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    const query = state.query;
    Object.assign(state, _cloneDeep(defPrivateDispatchDashboardViewState));
    if (route.name === routeNames.DISPATCH) {
      state.query = query; // preserve query while on the page
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: DispatchDashboardViewDataComposable) {
    state.data = reactive(viewData);
  }

  function setQuery(routeQuery: DispatchRouteQueryComposable) {
    state.query = reactive(routeQuery);
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.DISPATCH) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
    setQuery,
  };
}

function dispatchDashboardViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defDispatchDashboardViewState));
  const route = useRoute();
  const router = useRouter();

  const staticStore = useStaticStore();

  const _dispatchDashboardViewStore = usePrivateDispatchDashboardViewStore();

  const jobEditDialog = useJobEditDialog();

  const data = computed(function () {
    return _dispatchDashboardViewStore.data;
  });

  const query = computed(function () {
    return _dispatchDashboardViewStore.query;
  });

  const dispatchJobsTableData = computed(function () {
    return dispatchJobsTableHelpers.getTableData(
      data.value.dispatchJobs,
      dispatchJobSearchFormInitialData.value,
      data.value.dispatchJobsLocks,
      query.value.tab,
      staticStore.getServiceAreas,
      query.value.date,
    );
  });

  const dispatchDriversTableData = computed(function () {
    return dispatchDriversTableHelpers.getTableData(
      staticStore.getDrivers,
      data.value.dispatchJobs,
      dispatchDriverSearchFormInitialData.value,
      data.value.dispatchDriversMarks,
    );
  });

  const dispatchJobSearchFormInitialData =
    computed<DispatchJobSearchFormInitialData>(() => {
      return {
        search: query.value.jobSearch,
        serviceAreas: query.value.jobServiceAreas,
        serviceGroups: query.value.jobServiceGroups,
        moreFilters: {
          sites: query.value.jobSites,
          goods: query.value.jobGoods,
          switches: {
            isCanSchedule: query.value.isJobCanSchedule,
            isPayOnHold: query.value.isJobPayOnHold,
            isInvoice: query.value.isJobInvoice,
          },
        },
        date: query.value.date
          ? format(query.value.date, 'yyyy-MM-dd')
          : undefined,
      };
    });

  const dispatchDriverSearchFormInitialData =
    computed<DispatchDriverSearchFormInitialData>(() => {
      return {
        search: query.value.driverSearch,
        fleets: query.value.driverFleets,
        driverFilters: {
          type: query.value.driverType,
          zone: query.value.driverZone,
        },
      };
    });

  const activatedJob = computed(() => {
    if (!data.value.dispatchJobs) return;
    return data.value.dispatchJobs.find((dispatchJob) => {
      return dispatchJob.id === state.jobsTableModel.active;
    });
  });

  const selectedJobs = computed(() => {
    if (!data.value.dispatchJobs) return;
    const _selectedJobs = data.value.dispatchJobs.filter((dispatchJob) => {
      return state.jobsTableModel.select[dispatchJob.id];
    });
    return dispatchJobsTableHelpers.getTableData(
      _selectedJobs,
      dispatchJobSearchFormInitialData.value,
      data.value.dispatchJobsLocks,
      query.value.tab,
      staticStore.getServiceAreas,
      query.value.date,
    );
  });

  const driverCount = computed(() => {
    return dispatchDriversTableData.value.length;
  });

  const activatedJobId = computed(() => {
    if (!activatedJob.value) return NaN;
    return activatedJob.value.rockhopper_id;
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defDispatchDashboardViewState));
    _dispatchDashboardViewStore.$reset();
  }

  function init(
    viewData: DispatchDashboardViewDataComposable,
    routeQuery: DispatchRouteQueryComposable,
  ) {
    _dispatchDashboardViewStore.setData(viewData);
    _dispatchDashboardViewStore.setQuery(routeQuery);
  }

  function jobSearch(formData: DispatchJobSearchFormValidData): void {
    const query = locationQueryHelpers.buildDispatchJobSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.DISPATCH,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function driverSearch(formData: DispatchDriverSearchFormValidData): void {
    const query = locationQueryHelpers.buildDispatchDriverSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.DISPATCH,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function handleEditJobClick(): void {
    const job = activatedJob.value;
    if (!job) return;

    const jobs = dispatchJobsTableData.value.map((item) => ({
      jobId: item.jobId,
      jobDate: item.jobDate,
    }));

    jobEditDialog.open(
      job.rockhopper_id,
      job.rockhopper_date,
      jobs,
      data.value.refreshDispatchJobs,
    );
  }

  // TODO all other functions

  watch([() => data.value.dispatchJobs], (newValue, oldValue) => {
    state.bulkSelectedJobIds = [];
    state.selectedJobId = NaN;
  });

  watch(activatedJob, (newValue, oldValue) => {
    data.value.stopLocations = undefined;
    // Otherwise the previous job's failure still counts as an answer for this
    // one, and its map shows until the (debounced) fetch starts.
    data.value.stopLocationsError = undefined;
  });

  watchDebounced(
    activatedJob,
    (newValue, oldValue) => {
      state.stopDetailsActivatedTabNum = 1;
      if (!activatedJob.value) return;
      if (!data.value.refreshStopLocations) return;
      void data.value.refreshStopLocations({
        job_id: activatedJob.value.rockhopper_id,
        job_date: activatedJob.value.rockhopper_date,
      });
    },
    { debounce: 2500, maxWait: commonConstants.MAX_INT32 },
  );

  return {
    ...toRefs(state),
    data,
    query,
    dispatchJobsTableData,
    dispatchDriversTableData,
    dispatchJobSearchFormInitialData,
    dispatchDriverSearchFormInitialData,
    activatedJob,
    selectedJobs,
    driverCount,
    activatedJobId,
    $reset,
    init,
    jobSearch,
    driverSearch,
    handleEditJobClick,
  };
}

const usePrivateDispatchDashboardViewStore = defineStore(
  '_dispatchDashboardView',
  privateDispatchDashboardViewStoreSetup,
);

export const useDispatchDashboardViewStore = defineStore(
  'dispatchDashboardView',
  dispatchDashboardViewStoreSetup,
);
